// eslint-disable-next-line local-eslint-rules/enforce-namespace-modularity
import type { EndpointMethod, FilteredDashboardApiEndpointKeys, dashboardApiEndpoints } from "@dashboardApi/apis";

import { FetchUserEncodedError, isUserEncodedError } from "@lib/errors/FetchUserEncodedError";

import { iframeApiMessage } from "./constants";
import type { PostMessageFetcherParams, ApiRequestMessage, ApiResponseMessage } from "./types";
/**
 * This function is used to fetch data using MessageChannel and PostMessage APIs.
 *
 * One example usage of this is in our handling of passing data between a sandboxed iframe to prevent XSS attacks.
 * To avoid XSS attacks, we do not allow the editor domain to talk to the backend directly.
 * Instead, we send the request to the parent domain and let the parent domain make the request.
 * The parent domain will then send the response back to the editor domain.
 *
 * @example
 * ```
 *  const response = await postMessageFetcher({
 *    endpoint: "/engagement/:app_group_id/templated_preview",
 *    method: "POST",
 *    args: {
 *      app_group_id: "123",
 *      content: "456",
 *    },
 *    targetOrigin: "https://dashboard.braze.com",
 *  });
 * ```
 */
export const postMessageFetcher = <
  Method extends EndpointMethod,
  Endpoint extends FilteredDashboardApiEndpointKeys<typeof dashboardApiEndpoints, Method>,
  Result = unknown,
  Arguments = unknown,
>({
  endpoint,
  method,
  args,
  targetOrigin,
}: PostMessageFetcherParams<Endpoint, Method, Arguments>): Promise<Result> => {
  return new Promise((resolve, reject) => {
    const messageChannel = new MessageChannel();
    messageChannel.port1.onmessage = (event: MessageEvent<ApiResponseMessage>) => {
      if (event.data.type === iframeApiMessage.apiResponse) {
        if (event.data.error) {
          // Error object cannot be sent through MessageChannel directly. We receive a plain object with
          // the name and message and convert it back to an Error object.
          const errorData = event.data.error;
          if (errorData.errorCode !== undefined) {
            // Reconstruct a FetchUserEncodedError-like object
            const userEncodedErrorShape = isUserEncodedError(errorData.responseJson)
              ? errorData.responseJson
              : {
                  message: errorData.message,
                  // This is to match the shape of the error object returned from the server
                  error_code: errorData.errorCode,
                  meta: errorData.meta ?? {},
                };
            const error = new FetchUserEncodedError(
              errorData.statusCode ?? 500,
              errorData.message,
              userEncodedErrorShape
            );
            error.name = errorData.name;
            reject(error);
          } else {
            const error = new Error(errorData.message);
            error.name = errorData.name;
            reject(error);
          }
        } else {
          const { response } = event.data;
          resolve(response);
        }
        messageChannel.port1.close();
      }
    };

    const payload: ApiRequestMessage<Method, Endpoint, Arguments> = {
      type: iframeApiMessage.apiRequest,
      args,
      endpoint,
      method,
    };

    window.parent.postMessage(payload, targetOrigin, [messageChannel.port2]);
  });
};
